///
/// EarthBrowser
///
/// Created by Matt Giger on 1/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { BuoyNode } from "@/earthbrowser/dataset-distribution/buoys/BuoyNode"
import { BuoyObservationsTable, type BuoyObservationProperties } from "./BuoyObservationsTable"
import { BuoyStationsTable, type BuoyStationProperties } from "./BuoyStationsTable"
import type { BuoyEntry, BuoySource, BuoyStationType, BuoySteepness, DartMeasurementType } from "@shared-types/buoy"
import type { Point } from "@shared-types/geojson"
import { DatasetLayer, type ArrowRow, type DatasetSceneObject, type DatasetFeature, type DatasetManifest, type DatasetSyncConfig } from "../DatasetLayer"

export interface BuoyFeature extends BuoyEntry, DatasetFeature {
	id: string
}

/** Shape of a buoy_stations Arrow row (only fields we read) */
type StationRow = {
	station_id: string
	name: string
	lat: number
	lon: number
	type: string | null
	owner: string | null
	is_drifting: boolean
	pgm: string | null
	met: boolean
	currents: boolean
	water_quality: boolean
	dart: boolean
	buoycam: boolean
	source: string | null
	wmo_id: string | null
	coops_id: string | null
}

/** Shape of a buoy_observations Arrow row (only fields we read) */
type ObservationRow = {
	station_id: string
	time: string
	wind_direction: number | null
	wind_speed: number | null
	wind_gust: number | null
	air_temp: number | null
	pressure: number | null
	pressure_tendency: number | null
	visibility: number | null
	dewpoint: number | null
	wave_height: number | null
	wave_period: number | null
	avg_wave_period: number | null
	wave_direction: number | null
	water_temp: number | null
	lat: number | null
	lon: number | null
	swell_height: number | null
	swell_period: number | null
	swell_direction: number | null
	wind_wave_height: number | null
	wind_wave_period: number | null
	wind_wave_direction: number | null
	steepness: string | null
	water_column_height: number | null
	dart_measurement_type: number | null
	water_level: number | null
}

const cSteepnessClasses = new Set<BuoySteepness>(["VERY_STEEP", "STEEP", "AVERAGE", "SWELL"])

function parseSteepness(value: string | null): BuoySteepness | null {
	return value && cSteepnessClasses.has(value as BuoySteepness) ? value as BuoySteepness : null
}

function parseDartType(value: number | null): DartMeasurementType | null {
	return value === 1 || value === 2 || value === 3 ? value : null
}

export class BuoysDatasetLayer extends DatasetLayer<BuoyFeature> {
	private unsubscribeRedux?: () => void
	private lastVisibleState: boolean = true

	constructor(context: EarthBrowserContext) {
		super(context, 'buoys')

		// Subscribe to Redux for visibility changes
		this.subscribeToRedux()
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "buoy_stations",
			pollIntervalMs: 60 * 60_000, // 1 hour for observations
			needsSync: true,
			supportsDelta: false,
		}
	}

	/**
	 * Return sub-dataset IDs that this layer manages
	 */
	public getSubDatasetIds(): string[] {
		return ["buoy_observations"]
	}

	/**
	 * Apply checkpoint for a sub-dataset
	 */
	public async applySubDatasetCheckpoint(
		datasetId: string,
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		if (datasetId === "buoy_observations") {
			await this.applyBuoyObservationsCheckpoint(manifest, downloadArrowRows)
		}
	}

	public async createTable(db: Database): Promise<void> {
		await BuoyStationsTable.create(db)
		await BuoyObservationsTable.create(db)
	}

	public override async getStoredRowCount(datasetId: string): Promise<number | null> {
		if (datasetId === "buoy_stations") return BuoyStationsTable.getCount()
		if (datasetId === "buoy_observations") return BuoyObservationsTable.getCount()
		return null
	}

	/**
	 * Apply checkpoint for buoy_stations.
	 * Note: buoy_observations is synced separately via applyBuoyObservationsCheckpoint.
	 */
	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		console.log(`[BuoysDatasetLayer] Downloading stations shard: ${url}`)
		const rows = await downloadArrowRows(url)
		console.log(`[BuoysDatasetLayer] Parsed ${rows.length} station rows`)

		const stations = this.parseStationRows(rows)
		await BuoyStationsTable.replaceAll(stations)
	}

	/**
	 * Apply buoy_observations checkpoint (secondary dataset)
	 */
	public async applyBuoyObservationsCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		console.log(`[BuoysDatasetLayer] Downloading observations shard: ${url}`)
		const rows = await downloadArrowRows(url)
		console.log(`[BuoysDatasetLayer] Parsed ${rows.length} observation rows`)

		const observations = this.parseObservationRows(rows)
		await BuoyObservationsTable.replaceAll(observations)
	}

	public async onSyncComplete(): Promise<void> {
		// Refresh buoys scene objects
		await this.refresh()
	}

	private parseStationRows(arrowRows: ArrowRow[]): BuoyStationProperties[] {
		const rows = arrowRows as StationRow[]
		return rows.map(r => ({
			station_id: String(r.station_id),
			name: String(r.name),
			lat: Number(r.lat),
			lon: Number(r.lon),
			type: r.type ? String(r.type) : null,
			owner: r.owner ? String(r.owner) : null,
			is_drifting: Boolean(r.is_drifting),
			pgm: r.pgm ? String(r.pgm) : null,
			met: Boolean(r.met),
			currents: Boolean(r.currents),
			water_quality: Boolean(r.water_quality),
			dart: Boolean(r.dart),
			buoycam: Boolean(r.buoycam),
			source: (r.source ? String(r.source) : "ndbc") as BuoySource,
			wmo_id: r.wmo_id ? String(r.wmo_id) : null,
			coops_id: r.coops_id ? String(r.coops_id) : null,
		}))
	}

	private parseObservationRows(arrowRows: ArrowRow[]): BuoyObservationProperties[] {
		const rows = arrowRows as ObservationRow[]
		return rows.map(r => ({
			station_id: String(r.station_id),
			time: String(r.time),
			wind_direction: r.wind_direction != null ? Number(r.wind_direction) : null,
			wind_speed: r.wind_speed != null ? Number(r.wind_speed) : null,
			wind_gust: r.wind_gust != null ? Number(r.wind_gust) : null,
			air_temp: r.air_temp != null ? Number(r.air_temp) : null,
			pressure: r.pressure != null ? Number(r.pressure) : null,
			pressure_tendency: r.pressure_tendency != null ? Number(r.pressure_tendency) : null,
			visibility: r.visibility != null ? Number(r.visibility) : null,
			dewpoint: r.dewpoint != null ? Number(r.dewpoint) : null,
			wave_height: r.wave_height != null ? Number(r.wave_height) : null,
			wave_period: r.wave_period != null ? Number(r.wave_period) : null,
			avg_wave_period: r.avg_wave_period != null ? Number(r.avg_wave_period) : null,
			wave_direction: r.wave_direction != null ? Number(r.wave_direction) : null,
			water_temp: r.water_temp != null ? Number(r.water_temp) : null,
			lat: r.lat != null ? Number(r.lat) : null,
			lon: r.lon != null ? Number(r.lon) : null,
			swell_height: r.swell_height != null ? Number(r.swell_height) : null,
			swell_period: r.swell_period != null ? Number(r.swell_period) : null,
			swell_direction: r.swell_direction != null ? Number(r.swell_direction) : null,
			wind_wave_height: r.wind_wave_height != null ? Number(r.wind_wave_height) : null,
			wind_wave_period: r.wind_wave_period != null ? Number(r.wind_wave_period) : null,
			wind_wave_direction: r.wind_wave_direction != null ? Number(r.wind_wave_direction) : null,
			steepness: parseSteepness(r.steepness != null ? String(r.steepness) : null),
			water_column_height: r.water_column_height != null ? Number(r.water_column_height) : null,
			dart_measurement_type: parseDartType(r.dart_measurement_type != null ? Number(r.dart_measurement_type) : null),
			water_level: r.water_level != null ? Number(r.water_level) : null,
		}))
	}

	// ==================== Redux Subscription ====================

	private subscribeToRedux(): void {
		// Apply initial state
		this.lastVisibleState = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("buoy") ?? false
		this.visible = this.lastVisibleState

		// Subscribe to changes
		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const isVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("buoy") ?? false

			if (isVisible !== this.lastVisibleState) {
				this.lastVisibleState = isVisible
				this.visible = isVisible
			}
		})
	}

	// ==================== Scene Objects ====================

	public createSceneObject(feature: BuoyFeature): DatasetSceneObject<BuoyFeature> | undefined {
		// Create a BuoyNode (reusing existing implementation)
		const geoJsonFeature = {
			type: 'Feature' as const,
			id: feature.id,
			geometry: {
				type: 'Point' as const,
				coordinates: [feature.lon, feature.lat]
			} as Point,
			properties: feature,
		}

		return new BuoyNode(this.context, geoJsonFeature) as unknown as DatasetSceneObject<BuoyFeature>
	}

	/**
	 * Refresh buoys from the database
	 */
	public override async refresh(): Promise<void> {
		try {
			// Get all stations
			const stations = await BuoyStationsTable.getAll()
			console.log(`[BuoysDatasetLayer] Loaded ${stations.length} stations`)

			// Get latest observations for each station
			const observationsMap = await BuoyObservationsTable.getLatestByStation()
			console.log(`[BuoysDatasetLayer] Loaded ${observationsMap.size} observations`)

			// Combine stations with their latest observations
			const features: BuoyFeature[] = stations.map(station => {
				const observation = observationsMap.get(station.station_id)
				// A located observation (drifting buoys) is fresher than the station listing
				const located = observation?.lat != null && observation.lon != null

				return {
					id: station.station_id,
					station_id: station.station_id,
					name: station.name,
					lat: located ? observation.lat! : station.lat,
					lon: located ? observation.lon! : station.lon,
					type: (station.type as BuoyStationType | null) ?? null,
					owner: station.owner,
					is_drifting: station.is_drifting,
					pgm: station.pgm,
					met: station.met,
					currents: station.currents,
					water_quality: station.water_quality,
					dart: station.dart,
					buoycam: station.buoycam,
					source: station.source,
					wmo_id: station.wmo_id,
					coops_id: station.coops_id,
					latest_observation: observation ? { ...observation } : null,
				}
			})

			this.updateSceneObjects(features)
		} catch (error) {
			console.error(`[BuoysDatasetLayer] Error refreshing buoys:`, error)
		}
	}

	/**
	 * Override dispose to clean up Redux subscription
	 */
	override destroy(): void {
		if (this.unsubscribeRedux) {
			this.unsubscribeRedux()
		}
		super.destroy()
	}
}

